import { ThunkDispatch } from "@reduxjs/toolkit";
import { useThrowAsyncError } from "igfd-commons";
import { CircularProgressCenter } from "igfd-ui";

import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import { AdminModeButton } from "@dashboard/components/admin/AdminModeButton";
import { AppScaffold } from "@dashboard/components/core/Scaffold";
import { AxiosAuthTokenProvider, useAuthState } from "@dashboard/context";

import { NavigableErrorBoundary } from "../components/errors/ErrorBoundaries";
import { fetchAccessibleRestaurants } from "../store/actions/restaurantActions";
import { RestaurantReducerAction } from "../store/reducers/restaurantReducer";
import { ReduxState } from "../types/reduxState";
import { ThunkExtraArgument } from "../types/thunk";

/**
 * The layout of the dashboard for all pages that require authentication.
 *
 * We don't show the pages until the user is authenticated, and we know if the user
 * has access to any restaurants.
 */
const AuthenticatedLayoutComponent = ({
  fetchAccessibleRestaurants,
}: {
  fetchAccessibleRestaurants: (
    uid: string,
    onError: (error: Error) => void
  ) => void;
}) => {
  const { loading: authStateLoading, user, logout } = useAuthState();

  const initialRestaurantsLoading = useSelector(
    (state: ReduxState) => state.restaurant.initialRestaurantsLoading
  );

  const throwAsyncError = useThrowAsyncError();

  useEffect(() => {
    if (user) {
      // fetch happens after the user has been loaded (logged in)
      fetchAccessibleRestaurants(user.uid, throwAsyncError);
    }
  }, [user]);

  if (authStateLoading) {
    // the auth user hasn't been loaded yet.
    // this happens because the layout can be loaded before the auth state is initialised.
    return <CircularProgressCenter />;
  }

  if (!user) {
    // the user is not logged in.
    return <Navigate to="/login" />;
  }

  if (initialRestaurantsLoading) {
    // We check this after we know that the user is logged in.
    // Also we don't know if the user has access to any restaurants yet.
    return <CircularProgressCenter />;
  }

  return (
    <NavigableErrorBoundary>
      {/* This adds the firebase auth token to axios calls. */}
      <AxiosAuthTokenProvider>
        <AppScaffold user={user} logout={logout}>
          <AdminModeButton />
          <Outlet />
        </AppScaffold>
      </AxiosAuthTokenProvider>
    </NavigableErrorBoundary>
  );
};

const mapDispatchToProp = (
  dispatch: ThunkDispatch<
    ReduxState,
    ThunkExtraArgument,
    RestaurantReducerAction
  >
) => {
  return {
    fetchAccessibleRestaurants: (
      uid: string,
      onError: (error: Error) => void
    ) => {
      dispatch(fetchAccessibleRestaurants(uid, onError));
    },
  };
};
export const AuthenticatedLayout = connect(
  null,
  mapDispatchToProp
)(AuthenticatedLayoutComponent);
